/**
 * StatisticsModule Component
 *
 * Comprehensive warehouse picking statistics dashboard for BizClick WMS.
 * Displays performance metrics, analytics, and visualizations for warehouse operations.
 *
 * Features:
 * - Three view modes: Operator, Operator-Item, and Item statistics
 * - Date range filtering with quick preset options
 * - Sortable data tables with Norwegian formatting
 * - Toggle for time-related metrics visibility
 * - CSV export functionality
 * - Bar chart visualizations
 * - Footer totals and averages
 * - Loading and error states
 * - Access control (warehouse managers only)
 *
 * Layout:
 * ┌──────────────────────────────────────────────────────────┐
 * │ Header: Title and Description                             │
 * ├──────────────────────────────────────────────────────────┤
 * │ Controls:                                                 │
 * │  • View Mode Radio Buttons (3 options)                   │
 * │  • Date Range Filters (Start/End + Presets)             │
 * │  • Time Metrics Toggle Checkbox                          │
 * │  • Refresh & Export Buttons                              │
 * ├──────────────────────────────────────────────────────────┤
 * │ Data Table (Sortable columns, footer with totals)        │
 * ├──────────────────────────────────────────────────────────┤
 * │ Chart Visualization (Top performers bar chart)           │
 * └──────────────────────────────────────────────────────────┘
 *
 * @author BizClick WMS
 * @version 1.0
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart3,
  Download,
  RefreshCw,
  Calendar,
  Users,
  Package,
  TrendingUp,
  Clock,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import statisticsService from '../../../../services/statisticsService';
import {
  OperatorStatistics,
  OperatorItemStatistics,
  ItemStatistics,
  StatisticsView,
  StatisticsSummary
} from '../../../../types/statistics';
import { BackButton } from '../../../common/BackButton';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useSubModuleView } from '../../../../hooks/useSubModuleView';

// Type union for all statistics data types
type StatisticsData = OperatorStatistics | OperatorItemStatistics | ItemStatistics;

// Sort configuration
interface SortConfig {
  key: string | null;
  direction: 'asc' | 'desc';
}

// Chart metric options
type ChartMetric = 'sumOrderValue' | 'numberOfOrderLines' | 'sumQuantityPicked' | 'numberOfOrders';

// Resolved chart metric option (labels resolved via i18n inside the component)
interface ChartMetricOption {
  value: ChartMetric;
  label: string;
  yAxisLabel: string;
  isCurrency: boolean;
}

// Date preset definition
interface DatePreset {
  label: string;
  days?: number;
  isCurrentMonth?: boolean;
}

/**
 * Main Statistics Module Component
 */

interface StatisticsModuleProps {
  onBack?: () => void;
}

export const StatisticsModule: React.FC<StatisticsModuleProps> = ({ onBack }) => {
  const { t } = useTranslation();

  // Chart metric options (labels resolved via i18n)
  const chartMetricOptions = useMemo<ChartMetricOption[]>(() => [
    { value: 'sumOrderValue', label: t('manager.modules.statistics.charts.metrics.sumOrderValue', {}, 'Ordreverdi'), yAxisLabel: t('manager.modules.statistics.charts.axis.sumOrderValue', {}, 'Ordreverdi (kr)'), isCurrency: true },
    { value: 'numberOfOrderLines', label: t('manager.modules.statistics.charts.metrics.numberOfOrderLines', {}, 'Antall ordrelinjer'), yAxisLabel: t('manager.modules.statistics.charts.axis.numberOfOrderLines', {}, 'Antall ordrelinjer'), isCurrency: false },
    { value: 'sumQuantityPicked', label: t('manager.modules.statistics.charts.metrics.sumQuantityPicked', {}, 'Antall plukket'), yAxisLabel: t('manager.modules.statistics.charts.axis.sumQuantityPicked', {}, 'Antall plukket'), isCurrency: false },
    { value: 'numberOfOrders', label: t('manager.modules.statistics.charts.metrics.numberOfOrders', {}, 'Antall ordrer'), yAxisLabel: t('manager.modules.statistics.charts.axis.numberOfOrders', {}, 'Antall ordrer'), isCurrency: false },
  ], [t]);

  // View and filter state
  const [view, setView] = useSubModuleView<StatisticsView>('operator', { landing: 'operator' });
  const [startDate, setStartDate] = useState<Date>(getDefaultStartDate());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [showTimeMetrics, setShowTimeMetrics] = useState<boolean>(false);
  const [showChart, setShowChart] = useState<boolean>(true);
  const [chartMetric, setChartMetric] = useState<ChartMetric>('sumOrderValue');

  // Data state
  const [data, setData] = useState<StatisticsData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Sorting state
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'asc' });

  /**
   * Get default start date (7 days ago)
   */
  function getDefaultStartDate(): Date {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  }

  /**
   * Date preset configurations
   */
  const datePresets: DatePreset[] = useMemo(() => [
    { label: t('manager.modules.statistics.datePresets.today', {}, 'I dag'), days: 0 },
    { label: t('manager.modules.statistics.datePresets.yesterday', {}, 'I går'), days: 1 },
    { label: t('manager.modules.statistics.datePresets.last7Days', {}, 'Siste 7 dager'), days: 7 },
    { label: t('manager.modules.statistics.datePresets.last30Days', {}, 'Siste 30 dager'), days: 30 },
    { label: t('manager.modules.statistics.datePresets.currentMonth', {}, 'Inneværende måned'), isCurrentMonth: true }
  ], [t]);

  /**
   * Handle date preset selection
   */
  const handlePresetClick = useCallback((preset: DatePreset) => {
    const end = new Date();
    let start = new Date();

    if (preset.isCurrentMonth) {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else if (preset.days === 0) {
      // Today: both start and end are today
      start = new Date(end);
    } else if (preset.days === 1) {
      // Yesterday
      start = new Date(end);
      start.setDate(start.getDate() - 1);
      end.setDate(end.getDate() - 1);
    } else {
      // Last N days
      start = new Date(end);
      start.setDate(start.getDate() - (preset.days || 7));
    }

    setStartDate(start);
    setEndDate(end);
  }, []);

  /**
   * Fetch statistics data based on current view
   */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let result: StatisticsData[];

      switch (view) {
        case 'operator':
          result = await statisticsService.getOperatorStatistics(startDate, endDate);
          break;
        case 'operator-item':
          result = await statisticsService.getOperatorItemStatistics(startDate, endDate);
          break;
        case 'item':
          result = await statisticsService.getItemStatistics(startDate, endDate);
          break;
        default:
          throw new Error(t('manager.modules.statistics.errors.invalidView', {}, 'Ugyldig visningstype'));
      }

      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('manager.modules.statistics.errors.fetchFailed', {}, 'Kunne ikke hente statistikk');
      setError(errorMessage);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [view, startDate, endDate, t]);

  /**
   * Handle manual refresh
   */
  const handleRefresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Handle CSV export
   */
  const handleExport = useCallback(() => {
    if (data.length === 0) {
      setError(t('manager.modules.statistics.errors.noDataToExport', {}, 'Ingen data å eksportere'));
      return;
    }

    try {
      const viewLabels: Record<StatisticsView, string> = {
        'operator': 'operatoer',
        'operator-item': 'operatoer-vare',
        'item': 'vare'
      };

      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `statistikk-${viewLabels[view]}-${dateStr}.csv`;

      statisticsService.exportToCSV(data, filename);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('manager.modules.statistics.errors.exportFailed', {}, 'Kunne ikke eksportere data');
      setError(errorMessage);
    }
  }, [data, view, t]);

  /**
   * Calculate summary statistics
   */
  const summary = useMemo<StatisticsSummary>(() => {
    if (data.length === 0) {
      return {
        totalOrderValue: 0,
        totalQuantityPicked: 0,
        totalOrders: 0,
        totalOrderLines: 0,
        totalMinutesPicking: 0,
        averageMinutesPerLinePicking: 0,
        totalMinutesTotal: 0,
        averageMinutesPerLineTotal: 0
      };
    }

    const totals = data.reduce((acc, item) => ({
      totalOrderValue: acc.totalOrderValue + item.sumOrderValue,
      totalQuantityPicked: acc.totalQuantityPicked + item.sumQuantityPicked,
      totalOrders: acc.totalOrders + item.numberOfOrders,
      totalOrderLines: acc.totalOrderLines + item.numberOfOrderLines,
      totalMinutesPicking: acc.totalMinutesPicking + item.minutesPicking,
      totalMinutesTotal: acc.totalMinutesTotal + item.minutesTotal
    }), {
      totalOrderValue: 0,
      totalQuantityPicked: 0,
      totalOrders: 0,
      totalOrderLines: 0,
      totalMinutesPicking: 0,
      totalMinutesTotal: 0
    });

    // Calculate averages
    const avgMinutesPerLinePicking = data.reduce((sum, item) =>
      sum + item.minutesPerLinePicking, 0) / data.length;

    const avgMinutesPerLineTotal = data.reduce((sum, item) =>
      sum + item.minutesPerLineTotal, 0) / data.length;

    return {
      ...totals,
      averageMinutesPerLinePicking: avgMinutesPerLinePicking,
      averageMinutesPerLineTotal: avgMinutesPerLineTotal
    };
  }, [data]);

  /**
   * Handle column sort
   */
  const handleSort = useCallback((key: string) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  /**
   * Sort data based on current sort configuration
   */
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = (a as any)[sortConfig.key!];
      const bValue = (b as any)[sortConfig.key!];

      if (aValue === bValue) return 0;

      const comparison = aValue < bValue ? -1 : 1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig]);

  /**
   * Prepare chart data (top 10 performers)
   */
  const chartData = useMemo(() => {
    const topItems = [...data]
      .sort((a, b) => (b[chartMetric] as number) - (a[chartMetric] as number))
      .slice(0, 10);

    return topItems.map(item => {
      let label = '';

      if (view === 'operator') {
        const opItem = item as OperatorStatistics;
        label = opItem.operatorName || opItem.pickedBy;
      } else if (view === 'operator-item') {
        const opItemItem = item as OperatorItemStatistics;
        label = `${opItemItem.operatorName} - ${opItemItem.itemNo}`;
      } else {
        const itemItem = item as ItemStatistics;
        label = itemItem.itemNo;
      }

      return {
        name: label,
        value: item[chartMetric] as number
      };
    });
  }, [data, view, chartMetric]);

  const activeMetricOption = chartMetricOptions.find(o => o.value === chartMetric)!;

  /**
   * Get color for chart bar based on index
   */
  const getBarColor = (index: number): string => {
    const colors = [
      '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
      '#06b6d4', '#6366f1', '#ef4444', '#14b8a6', '#f97316'
    ];
    return colors[index % colors.length];
  };

  /**
   * Render sort indicator
   */
  const renderSortIndicator = (key: string) => {
    if (sortConfig.key !== key) {
      return <ChevronUp className="w-4 h-4 opacity-30" />;
    }
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="w-4 h-4 text-blue-600" />
      : <ChevronDown className="w-4 h-4 text-blue-600" />;
  };

  /**
   * Render table header
   */
  const renderTableHeader = () => {
    const commonColumns = [
      { key: 'sumOrderValue', label: t('manager.modules.statistics.columns.sumOrderValue', {}, 'Ordreverdi') },
      { key: 'sumQuantityPicked', label: t('manager.modules.statistics.columns.sumQuantityPicked', {}, 'Antall plukket') },
      { key: 'numberOfOrders', label: t('manager.modules.statistics.columns.numberOfOrders', {}, 'Antall ordre') },
      { key: 'numberOfOrderLines', label: t('manager.modules.statistics.columns.numberOfOrderLines', {}, 'Antall ordrelinjer') }
    ];

    const timeColumns = showTimeMetrics ? [
      { key: 'minutesPicking', label: t('manager.modules.statistics.columns.minutesPicking', {}, 'Plukketid (min)') },
      { key: 'minutesPerLinePicking', label: t('manager.modules.statistics.columns.minutesPerLinePicking', {}, 'Min/linje plukk') },
      { key: 'minutesTotal', label: t('manager.modules.statistics.columns.minutesTotal', {}, 'Total tid (min)') },
      { key: 'minutesPerLineTotal', label: t('manager.modules.statistics.columns.minutesPerLineTotal', {}, 'Min/linje total') }
    ] : [];

    let specificColumns: { key: string; label: string }[] = [];

    if (view === 'operator') {
      specificColumns = [
        { key: 'pickedBy', label: t('manager.modules.statistics.columns.operatorId', {}, 'Operatør ID') },
        { key: 'operatorName', label: t('common.fields.name', {}, 'Navn') }
      ];
    } else if (view === 'operator-item') {
      specificColumns = [
        { key: 'pickedBy', label: t('manager.modules.statistics.columns.operatorId', {}, 'Operatør ID') },
        { key: 'operatorName', label: t('common.fields.name', {}, 'Navn') },
        { key: 'itemNo', label: t('manager.modules.statistics.columns.itemNo', {}, 'Varenr') },
        { key: 'description1', label: t('common.fields.description', {}, 'Beskrivelse') }
      ];
    } else {
      specificColumns = [
        { key: 'itemNo', label: t('manager.modules.statistics.columns.itemNo', {}, 'Varenr') },
        { key: 'description1', label: t('common.fields.description', {}, 'Beskrivelse') }
      ];
    }

    const allColumns = [...specificColumns, ...commonColumns, ...timeColumns];

    return (
      <thead className="bg-gray-50 sticky top-0 z-10">
        <tr>
          {allColumns.map(col => (
            <th
              key={col.key}
              onClick={() => handleSort(col.key)}
              className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-1">
                {col.label}
                {renderSortIndicator(col.key)}
              </div>
            </th>
          ))}
        </tr>
      </thead>
    );
  };

  /**
   * Render table row
   */
  const renderTableRow = (item: StatisticsData, index: number) => {
    const formatValue = (key: string, value: any) => {
      if (key === 'sumOrderValue') {
        return statisticsService.formatCurrency(value);
      }
      if (key.includes('minutes') || key.includes('Minutes')) {
        return statisticsService.formatNumber(value, 2);
      }
      if (typeof value === 'number') {
        return statisticsService.formatNumber(value, 0);
      }
      return value || '-';
    };

    const getValue = (key: string) => {
      return formatValue(key, (item as any)[key]);
    };

    return (
      <tr
        key={index}
        className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}
      >
        {view === 'operator' && (
          <>
            <td className="px-4 py-3 text-sm text-gray-900">{getValue('pickedBy')}</td>
            <td className="px-4 py-3 text-sm text-gray-900">{getValue('operatorName')}</td>
          </>
        )}
        {view === 'operator-item' && (
          <>
            <td className="px-4 py-3 text-sm text-gray-900">{getValue('pickedBy')}</td>
            <td className="px-4 py-3 text-sm text-gray-900">{getValue('operatorName')}</td>
            <td className="px-4 py-3 text-sm text-gray-900">{getValue('itemNo')}</td>
            <td className="px-4 py-3 text-sm text-gray-900">{getValue('description1')}</td>
          </>
        )}
        {view === 'item' && (
          <>
            <td className="px-4 py-3 text-sm text-gray-900">{getValue('itemNo')}</td>
            <td className="px-4 py-3 text-sm text-gray-900">{getValue('description1')}</td>
          </>
        )}
        <td className="px-4 py-3 text-sm text-gray-900">{getValue('sumOrderValue')}</td>
        <td className="px-4 py-3 text-sm text-gray-900">{getValue('sumQuantityPicked')}</td>
        <td className="px-4 py-3 text-sm text-gray-900">{getValue('numberOfOrders')}</td>
        <td className="px-4 py-3 text-sm text-gray-900">{getValue('numberOfOrderLines')}</td>
        {showTimeMetrics && (
          <>
            <td className="px-4 py-3 text-sm text-gray-900">{getValue('minutesPicking')}</td>
            <td className="px-4 py-3 text-sm text-gray-900">{getValue('minutesPerLinePicking')}</td>
            <td className="px-4 py-3 text-sm text-gray-900">{getValue('minutesTotal')}</td>
            <td className="px-4 py-3 text-sm text-gray-900">{getValue('minutesPerLineTotal')}</td>
          </>
        )}
      </tr>
    );
  };

  /**
   * Render table footer with totals
   */
  const renderTableFooter = () => {
    const colSpan = view === 'operator' ? 2 : view === 'operator-item' ? 4 : 2;

    return (
      <tfoot className="bg-blue-50 font-semibold sticky bottom-0">
        <tr>
          <td colSpan={colSpan} className="px-4 py-3 text-sm text-gray-900">
            {t('manager.modules.statistics.labels.totalAverage', {}, 'Totalt / Gjennomsnitt')}
          </td>
          <td className="px-4 py-3 text-sm text-gray-900">
            {statisticsService.formatCurrency(summary.totalOrderValue)}
          </td>
          <td className="px-4 py-3 text-sm text-gray-900">
            {statisticsService.formatNumber(summary.totalQuantityPicked, 0)}
          </td>
          <td className="px-4 py-3 text-sm text-gray-900">
            {statisticsService.formatNumber(summary.totalOrders, 0)}
          </td>
          <td className="px-4 py-3 text-sm text-gray-900">
            {statisticsService.formatNumber(summary.totalOrderLines, 0)}
          </td>
          {showTimeMetrics && (
            <>
              <td className="px-4 py-3 text-sm text-gray-900">
                {statisticsService.formatNumber(summary.totalMinutesPicking, 2)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-900">
                {statisticsService.formatNumber(summary.averageMinutesPerLinePicking, 2)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-900">
                {statisticsService.formatNumber(summary.totalMinutesTotal, 2)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-900">
                {statisticsService.formatNumber(summary.averageMinutesPerLineTotal, 2)}
              </td>
            </>
          )}
        </tr>
      </tfoot>
    );
  };

  /**
   * Initial data load
   */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <BackButton onClick={onBack || (() => {})} className="text-white hover:bg-white/20" />
          <BarChart3 className="w-8 h-8" />
          <h1 className="text-2xl font-bold">{t('manager.modules.statistics.title', {}, 'Statistikk')}</h1>
        </div>
        <p className="text-blue-100">
          {t('manager.modules.statistics.subtitle', { '0': statisticsService.formatDate(startDate), '1': statisticsService.formatDate(endDate) }, `Lagerstatistikk og ytelsesindikatorer for periode ${statisticsService.formatDate(startDate)} - ${statisticsService.formatDate(endDate)}`)}
        </p>
      </div>

      {/* View Mode Selection */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('manager.modules.statistics.labels.viewType', {}, 'Visningstype')}
        </label>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setView('operator')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              view === 'operator'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Users className="w-4 h-4" />
            {t('manager.modules.statistics.views.operator', {}, 'Statistikk per operatør')}
          </button>
          <button
            onClick={() => setView('operator-item')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              view === 'operator-item'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Users className="w-4 h-4" />
            <Package className="w-4 h-4" />
            {t('manager.modules.statistics.views.operatorItem', {}, 'Statistikk per operatør og vare')}
          </button>
          <button
            onClick={() => setView('item')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              view === 'item'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Package className="w-4 h-4" />
            {t('manager.modules.statistics.views.item', {}, 'Statistikk per vare')}
          </button>
        </div>
      </div>

      {/* Date Range and Controls */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-4">
        {/* Date Inputs */}
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manager.modules.statistics.labels.fromDate', {}, 'Fra dato')}
            </label>
            <div className="relative">
              <input
                type="date"
                value={statisticsService.formatDateParam(startDate)}
                onChange={(e) => e.target.value && setStartDate(new Date(`${e.target.value}T00:00:00`))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Calendar className="absolute right-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manager.modules.statistics.labels.toDate', {}, 'Til dato')}
            </label>
            <div className="relative">
              <input
                type="date"
                value={statisticsService.formatDateParam(endDate)}
                onChange={(e) => e.target.value && setEndDate(new Date(`${e.target.value}T00:00:00`))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Calendar className="absolute right-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Date Presets */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('manager.modules.statistics.labels.quickSelect', {}, 'Hurtigvalg')}
          </label>
          <div className="flex flex-wrap gap-2">
            {datePresets.map((preset, index) => (
              <button
                key={index}
                onClick={() => handlePresetClick(preset)}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Controls Row */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t border-gray-200">
          <div className="flex items-center gap-4">
            {/* Time Metrics Toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showTimeMetrics}
                onChange={(e) => setShowTimeMetrics(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <Clock className="w-4 h-4 text-gray-600" />
              <span className="text-sm text-gray-700">{t('manager.modules.statistics.labels.showTimeMetrics', {}, 'Vis tidsmetrikker')}</span>
            </label>

            {/* Chart Toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showChart}
                onChange={(e) => setShowChart(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              {showChart ? <Eye className="w-4 h-4 text-gray-600" /> : <EyeOff className="w-4 h-4 text-gray-600" />}
              <span className="text-sm text-gray-700">{t('manager.modules.statistics.labels.showChart', {}, 'Vis diagram')}</span>
            </label>
          </div>

          <div className="flex items-center gap-2">
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {t('common.buttons.refresh', {}, 'Oppdater')}
            </button>

            {/* Export Button */}
            <button
              onClick={handleExport}
              disabled={loading || data.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-4 h-4" />
              {t('manager.modules.statistics.buttons.exportCsv', {}, 'Eksporter CSV')}
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">{t('common.status.error', {}, 'Feil')}</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-gray-600">{t('manager.modules.statistics.messages.loading', {}, 'Henter statistikk...')}</p>
          </div>
        </div>
      )}

      {/* Data Table */}
      {!loading && data.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <div className="max-h-[600px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                {renderTableHeader()}
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedData.map((item, index) => renderTableRow(item, index))}
                </tbody>
                {renderTableFooter()}
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && data.length === 0 && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center gap-4 text-gray-500">
            <TrendingUp className="w-12 h-12 opacity-50" />
            <p className="text-lg font-medium">{t('manager.modules.statistics.messages.noDataTitle', {}, 'Ingen data funnet')}</p>
            <p className="text-sm text-center max-w-md">
              {t('manager.modules.statistics.messages.noDataDescription', {}, 'Det finnes ingen statistikkdata for valgt periode og visning. Prøv å endre datointervall eller visningstype.')}
            </p>
          </div>
        </div>
      )}

      {/* Chart Visualization */}
      {!loading && data.length > 0 && showChart && chartData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {t('manager.modules.statistics.charts.top10', {}, 'Topp 10')}
            </h3>
            <select
              value={chartMetric}
              onChange={(e) => setChartMetric(e.target.value as ChartMetric)}
              className="ml-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {chartMetricOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                label={{ value: activeMetricOption.yAxisLabel, angle: -90, position: 'insideLeft' }}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value) => {
                  const val = (value as number) ?? 0;
                  if (activeMetricOption.isCurrency) {
                    return [statisticsService.formatCurrency(val), activeMetricOption.label];
                  }
                  return [statisticsService.formatNumber(val, 0), activeMetricOption.label];
                }}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="value" name={activeMetricOption.label} radius={[8, 8, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(index)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default StatisticsModule;
